/**
 * useVerificationAvailability — TanStack Query hook for
 * AgeVerificationModal.tsx. Structural-debt backlog #8 (TanStack Query
 * migration).
 *
 * Deliberately reuses queryKeys.verification.registrationAvailability()
 * and useRegister.ts's exact queryFn shape ({diditAvailable,
 * twilioAvailable}, same primary/fallback chain) rather than a new key --
 * same two endpoints, same shape once the bug below is fixed, real
 * cache-sharing value (a user landing on this modal right after
 * registering may already have this cached).
 *
 * Bugfix folded in: the pre-migration AgeVerificationModal read
 * `fallback.data?.available` on the ageVerificationAPI.checkDiditStatus()
 * fallback response, but getDiditStatus (backend) returns `configured`,
 * not `available` -- confirmed via ageVerificationController.ts:789-792.
 * `available` was always undefined, so the fallback path always resolved
 * to `diditAvailable: false` regardless of real Didit configuration.
 * useRegister.ts's own copy of this exact fallback chain already reads
 * `.configured` correctly; this migration fixes AgeVerificationModal to
 * match (bug-registry entry filed alongside this change).
 */
import { useQuery } from '@tanstack/react-query';
import { verificationAPI, ageVerificationAPI } from '../../services/api';
import { queryKeys } from './queryKeys';
import { unwrapResponse } from '../../utils/unwrapResponse';

export function useVerificationAvailability(enabled: boolean) {
  return useQuery<{ diditAvailable: boolean; twilioAvailable: boolean }>({
    queryKey: queryKeys.verification.registrationAvailability(),
    queryFn: async () => {
      try {
        const response = await verificationAPI.getStatus();
        const unwrapped = unwrapResponse<any>(response.data);
        const { services } = unwrapped ?? response.data;
        return { diditAvailable: !!services.didit, twilioAvailable: !!services.twilio };
      } catch {
        try {
          const fallback = await ageVerificationAPI.checkDiditStatus();
          return { diditAvailable: !!fallback.data.configured, twilioAvailable: false };
        } catch {
          return { diditAvailable: false, twilioAvailable: false };
        }
      }
    },
    enabled,
    retry: false,
  });
}
